import { QuestionType } from "../types/question";
import {
  QuestionBreakdown,
  QuestionBreakdownDemographic,
  QuestionBreakdownDemographicGroup,
  QuestionBreakdownFilterOptions,
} from "../types/workplaceCulture";
import { questionBreakdownColors } from "./graphs";

const LEGEND_WIDTH = 120;
interface SeriesData {
  text?: string;
  rawCount: number;
  y: number;
  n: number;
}

interface SkipSeriesData {
  label: string;
  data: SeriesData[];
}

export const getGroupedResponses = (
  chapterQuestionResponses: QuestionBreakdown[],
  useCombinedOptions = true
): {
  [key: string]: QuestionBreakdown[];
} => {
  return chapterQuestionResponses.reduce(
    (acc: { [key: string]: QuestionBreakdown[] }, questionBreakdown) => {
      const isMultiSelectQuestion =
        questionBreakdown.kind === QuestionType.MultiSelect;
      const key = isMultiSelectQuestion
        ? questionBreakdown.text
        : questionBreakdown.preamble || "";

      if (!acc[key]) {
        acc[key] = [];
      }

      if (isMultiSelectQuestion) {
        // We want to show each option of a MultiSelectQuestion as a separate question
        // So we map the answers to a question format so we can display them in the tabs
        let options: QuestionBreakdownFilterOptions[] | undefined;
        for (const demographicGroup of questionBreakdown.demographic_groups) {
          if (demographicGroup.demographics?.length !== 0) {
            options =
              demographicGroup.demographics?.[0][
                useCombinedOptions ? "combined_options" : "options"
              ];
            break;
          }
        }

        const multiAnswerQuestions = options?.map((answer) => {
          const filteredDemographicGroups = getQuestionDemographicGroups(
            questionBreakdown.demographic_groups,
            useCombinedOptions
              ? answer.option_ids?.[0] || ""
              : answer.reference_id || ""
          );

          const answerMappedToQuestion: QuestionBreakdown = {
            text: answer.text,
            reference_id: useCombinedOptions
              ? answer.option_ids?.[0] || ""
              : answer.reference_id || "",
            kind: questionBreakdown.kind,
            demographic_groups: filteredDemographicGroups,
            total_responses: questionBreakdown.total_responses,
          };

          return answerMappedToQuestion;
        });

        multiAnswerQuestions?.forEach((question) => {
          acc[key].push(question);
        });
      } else {
        acc[key].push(questionBreakdown);
      }

      return acc;
    },
    {}
  );
};

export const getQuestionDemographicGroups = (
  demographicGroups: QuestionBreakdownDemographicGroup[],
  answerReferenceId: string
): QuestionBreakdownDemographicGroup[] => {
  return demographicGroups.map((demographicGroup) => {
    const newDemographics = demographicGroup.demographics?.map((group) => {
      const newOptions = group.options.filter(
        (option) => option.reference_id === answerReferenceId
      );

      const newCombinedOptions = group.combined_options.filter((option) =>
        option.option_ids.includes(answerReferenceId)
      );

      return {
        label: group.label,
        reference_id: group.reference_id,
        engagement: group.engagement,
        options: newOptions,
        combined_options: newCombinedOptions,
        is_skip: group.is_skip,
      };
    });
    return {
      label: demographicGroup.label,
      demographics: newDemographics,
      is_custom: demographicGroup.is_custom,
      reference_id: demographicGroup.reference_id,
      is_combined: demographicGroup.is_combined,
    };
  });
};

export const doesQuestionHaveAnswers = (
  questionBreakdown?: QuestionBreakdown
) => {
  if (questionBreakdown) {
    for (const demographicGroup of questionBreakdown.demographic_groups) {
      if (Array.isArray(demographicGroup.demographics)) {
        for (const demographic of demographicGroup.demographics) {
          const options = demographic.options || [];
          const combinedOptions = demographic.combined_options || [];
          const allOptions = [...options, ...combinedOptions];
          const hasAnswers = allOptions.some((option) => option.count !== null);
          if (hasAnswers) {
            return true;
          }
        }
      }
    }
  }
  return false;
};

export const updateFilters = (selectedFilters: number[], index: number) => {
  if (selectedFilters.includes(index)) {
    return [...selectedFilters.filter((filterIndex) => filterIndex !== index)];
  } else {
    return [...selectedFilters, index].sort((a, b) => a - b);
  }
};

export const getQuestionResponseChartData = (
  t: any,
  breakdownDemographicGroup?: QuestionBreakdownDemographicGroup,
  useCombinedOptions = true,
  showIsSkip = false,
  isMultiSelect = false,
  customLegendOptions?: Highcharts.LegendOptions,
  customHeight?: number
): { chartData: Highcharts.Options; skipSeriesData: SkipSeriesData[] } => {
  const {
    series,
    xAxisOptions,
    noValueDemographics: noValueGroups,
    skipSeriesData,
  } = transformQuestionToChartSeries(
    breakdownDemographicGroup,
    useCombinedOptions,
    showIsSkip,
    isMultiSelect
  );
  // Compute the highest count from the series data
  let computedMax = 0;
  if (series && series.length) {
    // Flatten all 'data' arrays into an array of y-values
    const dataVals = series.flatMap((s) =>
      (s.data as Array<{ y: number }>).map((point) => point.y)
    );

    if (dataVals.length) {
      computedMax = Math.max(...dataVals);
    }
  }

  // Build a subtitle listing any skipped groups
  let skippedTitle = "";
  if (noValueGroups.length > 0) {
    skippedTitle =
      t("chapterPage.breakdown.chart.subtitleSkipped") +
      " " +
      noValueGroups.join(", ");
  }

  let subtitleText = "";
  if (computedMax < 50) {
    subtitleText = t("chapterPage.breakdown.chart.subtitleScalingNote");
  }

  let yAxisMax = 100;
  if (computedMax < 50 && computedMax >= 15) {
    yAxisMax = 50;
  } else if (computedMax < 15) {
    yAxisMax = 15;
  }

  const options: Highcharts.Options = {
    title: {
      text: breakdownDemographicGroup?.label,
      align: "left",
      style: {
        fontSize: "14px",
        fontFamily: "inherit",
        fontWeight: "bold",
      },
    },
    subtitle: {
      text: subtitleText,
      align: "right",
    },
    chart: {
      type: "column",
      height: customHeight || 300,
      zooming: {
        type: "y",
        resetButton: {
          position: {
            align: "right",
            verticalAlign: "top",
          },
        },
      },
    },
    xAxis: {
      categories: xAxisOptions,
      title: {
        text: skippedTitle,
        align: "high",
        x: LEGEND_WIDTH + 20,
      },
    },
    yAxis: {
      min: 0,
      max: yAxisMax,
      gridLineWidth: 0,
      title: {
        text: t("chapterPage.breakdown.chart.yAxisTitle"),
      },
    },
    legend: customLegendOptions || {
      enabled: true,
      align: "right",
      verticalAlign: "middle",
      width: LEGEND_WIDTH,
      itemStyle: {
        textOverflow: "wrap",
      },
    },
    plotOptions: {
      column: {
        pointWidth: 20,
        dataLabels: {
          enabled: true,
          formatter: function () {
            return `${Math.round(this.y as number)}%`;
          },
          style: {
            fontWeight: "400",
          },
        },
      },
    },
    tooltip: {
      formatter: function () {
        const rawCount = (this.point as any).options?.rawCount || 0;
        const n = (this.point as any).options?.n || 0;
        return `<b>${this.series.name} - ${Math.round(
          this.y as number
        )}%</b><br>
              n = ${n}<br>
              ${t("chapterPage.breakdown.chart.rawCount")} = ${rawCount}<br>`;
      },
    },
    series: series,
  };

  return { chartData: options, skipSeriesData };
};

export const transformQuestionToChartSeries = (
  breakdownDemographicGroup?: QuestionBreakdownDemographicGroup,
  useCombinedOptions = true,
  showIsSkip = false,
  isMultiSelect = false
): {
  series: Highcharts.SeriesColumnOptions[];
  xAxisOptions: string[];
  noValueDemographics: string[];
  skipSeriesData: SkipSeriesData[];
} => {
  if (!breakdownDemographicGroup) {
    return {
      series: [],
      xAxisOptions: [],
      noValueDemographics: [],
      skipSeriesData: [],
    };
  }

  // Sort demographics so that is_skip options are always last
  const demographics =
    breakdownDemographicGroup.demographics?.sort((a, b) => {
      if (a.is_skip && !b.is_skip) return 1;
      if (!a.is_skip && b.is_skip) return -1;
      return 0;
    }) || [];

  // Filter out any demographics that have null total in all non-skip options,
  // that means that demographic did not reach the threshold, we also filter out skip options
  // if showIsSkip is false
  let noValueDemographics: string[] = [];
  let demographicsWithValues = demographics.filter((demographic) => {
    const options = getOptionsFromDemographic(demographic, useCombinedOptions);

    const hasValues = options.some((option) => {
      const relevant = showIsSkip || !option.is_skip;
      return relevant && option.count !== null;
    });

    if (!hasValues) {
      noValueDemographics.push(demographic.label);
    }

    return hasValues;
  });

  // If all groups are filtered out, its because the only available option is the skip option
  // In that case, we want to show the skip group
  if (noValueDemographics.length === demographics.length) {
    noValueDemographics = [];
    demographicsWithValues = demographics.filter((group) => {
      const options = getOptionsFromDemographic(group, useCombinedOptions);

      const hasValues = options.some((option) => {
        return option.count !== null;
      });

      if (!hasValues) {
        noValueDemographics.push(group.label);
      }

      return hasValues;
    });
  }

  // Collect all unique non-skip combined option texts from the filtered groups
  const uniqueFilterOptions = new Set<string>();
  const onlyIsSkipAvailable =
    (demographicsWithValues.length === 1 &&
      demographicsWithValues[0].is_skip) ||
    (demographicsWithValues[0]?.options.length === 1 &&
      demographicsWithValues[0].options[0].is_skip);
  const includeIsSkip = showIsSkip || onlyIsSkipAvailable;

  demographicsWithValues.forEach((group) => {
    const options = getOptionsFromDemographic(group, useCombinedOptions);
    const relevantOptions = isMultiSelect
      ? options
      : options.filter((o) => !o.is_skip);

    relevantOptions.forEach((option) => {
      uniqueFilterOptions.add(option.text);
    });
  });

  const xAxisOptions = Array.from(uniqueFilterOptions);

  // Collect skip-option data across all groups
  const skipSeriesMap: Record<string, SeriesData[]> = {};
  if (
    (demographicsWithValues.length >= 1 &&
      demographicsWithValues[0]?.options.length > 1) ||
    !includeIsSkip
  ) {
    demographicsWithValues.forEach((group) => {
      const options = getOptionsFromDemographic(group, useCombinedOptions);
      options
        .filter((option) => option.is_skip)
        .forEach((option) => {
          const raw = option.count ?? 0;
          const percentage = group.engagement
            ? (raw / group.engagement) * 100
            : 0;
          if (!skipSeriesMap[option.text]) skipSeriesMap[option.text] = [];
          skipSeriesMap[option.text].push({
            text: group.label,
            rawCount: raw,
            y: Math.round(percentage),
            n: group.engagement,
          });
        });
    });
  }
  const skipSeriesData: SkipSeriesData[] = Object.entries(skipSeriesMap).map(
    ([label, data]) => ({ label, data })
  );

  // Build one series per filtered group

  const series: Highcharts.SeriesColumnOptions[] = demographicsWithValues.map(
    (group, i) => {
      const options = getOptionsFromDemographic(group, useCombinedOptions);

      const data: SeriesData[] = xAxisOptions.map((text) => {
        const match = includeIsSkip
          ? options.find((opt) => opt.text === text)
          : options.find((opt) => opt.text === text && !opt.is_skip);

        const groupEngagement = group.engagement || 1;

        const rawCount = match && match.count ? match.count : 0;
        const percentage = (rawCount / groupEngagement) * 100;

        return {
          y: percentage,
          rawCount,
          n: groupEngagement,
        };
      });

      return {
        type: "column",
        name: group.label,
        color: questionBreakdownColors[i],
        data,
        visible: includeIsSkip || !group.is_skip,
        showInLegend: includeIsSkip || !group.is_skip,
      };
    }
  );

  return {
    series,
    xAxisOptions,
    noValueDemographics,
    skipSeriesData,
  };
};

const getOptionsFromDemographic = (
  demographic: QuestionBreakdownDemographic,
  useCombinedOptions: boolean
): QuestionBreakdownFilterOptions[] => {
  return useCombinedOptions
    ? demographic.combined_options
    : demographic.options;
};
